import type { MinesweeperView } from '@cardengine/shared';
import { MinesweeperBoard } from '../components/MinesweeperBoard.js';
import { useStore } from '../store.js';
import { PaperRoom } from './PaperRoom.js';

/**
 * Minesweeper at the table.
 *
 * Thin, like `NoughtsRoom`: the table is `PaperRoom` and the game is
 * `MinesweeperBoard`, and what is left here is the translation between them.
 *
 * `PaperRoom` is on the paper shelf by name and by history, but it was written
 * as the *grid* table — "nothing below knows what a square is" — and that is
 * what it turns out to be. An arcade game reuses it unchanged apart from the
 * two things a game played alone does not have: no seat strip, and no chat tab.
 *
 * And one thing it deliberately declines: `result` is always null, so no modal
 * ever goes up. On every other game here the result screen is the payoff — the
 * board behind it has said everything it is going to. Minesweeper is the
 * opposite. The board at the end is the *most* informative it has been all
 * game: the mine you stepped on is marked, every other one is shown, and the
 * flags you got wrong are crossed. That is the entire lesson, and covering it
 * with a dialogue to announce a fact the board is already displaying is taking
 * the lesson away to tell you the score.
 *
 * So the ending goes where it does not obscure anything: the face turns, the
 * status line under the machine reads it out, and the panel offers another
 * board in the row the flag switch was using. Which is, not coincidentally,
 * exactly what the original did.
 */
export function MinesweeperRoom() {
  const view = useStore((s) => s.minesweeper);
  const lobby = useStore((s) => s.lobby);

  if (!view || !lobby) {
    return (
      <main className="game">
        <p className="game__loading">Laying the board…</p>
      </main>
    );
  }

  const cleared = lobby.members[0]?.wins ?? 0;

  return (
    <PaperRoom
      name="Minesweeper"
      seats={[]}
      chat={false}
      variant="arcade"
      status={describeProgress(view)}
      board={<MinesweeperBoard view={view} outcome={describeOutcome(view, cleared)} />}
      finished={view.phase !== 'playing'}
      result={null}
    />
  );
}

/**
 * The one line in the header.
 *
 * Squares left rather than a percentage: the number that matters is how many
 * are still shut, and near the end that is the only figure worth reading. A
 * percentage would also move in visible jumps on a small board, which reads as
 * progress being lost rather than as arithmetic.
 */
function describeProgress(view: MinesweeperView): string {
  const left = view.safeCount - view.revealedCount;
  const squares = (n: number) => `${n} ${n === 1 ? 'square' : 'squares'}`;

  if (view.phase === 'won') return `Cleared · all ${view.mineCount} mines accounted for`;
  if (view.phase === 'lost') return `${squares(left)} were left`;
  if (view.revealedCount === 0) return `${view.mineCount} mines · open anywhere to start`;

  return `${squares(left)} left · ${view.mineCount} mines`;
}

/**
 * How the board ended, in the player's own terms — one line, for the status
 * strip under the machine.
 *
 * A loss is written to be read rather than consoled. How close it was is the
 * interesting part, and "bad luck" about a board you misread is worse than
 * saying nothing: the whole claim Minesweeper makes is that the numbers were
 * telling you, so the line that ends the game does not pretend otherwise.
 *
 * It is a sentence rather than a title and a body because it has to fit on one
 * line under the board. That constraint is a good one — the modal version of
 * this was two paragraphs saying the same thing at more length.
 */
function describeOutcome(view: MinesweeperView, cleared: number): string | null {
  if (view.phase === 'playing') return null;

  const sitting =
    cleared > 1 ? ` ${cleared} boards cleared in this sitting.` : '';

  if (view.phase === 'won') {
    return `Cleared in ${view.moveNumber} moves, all ${view.mineCount} mines still where you left them.${sitting}`;
  }

  const short = view.safeCount - view.revealedCount;

  return short <= 3
    ? `That close — ${short} ${short === 1 ? 'square' : 'squares'} off clearing it.`
    : `${short} squares still shut. The numbers around that one were saying something; they usually are.`;
}
